import React, { useState } from 'react';
import { Tag, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PromoCodeInput = ({ orderTotal, customerEmail, onPromoApplied, appliedPromo }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleValidate = async () => {
    if (!code.trim()) {
      setError('Veuillez entrer un code promo');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/promo-codes/validate`, {
        code: code.trim(),
        order_total: orderTotal,
        customer_email: customerEmail
      });

      if (response.data.valid) {
        setSuccess(response.data.message);
        onPromoApplied({
          code: response.data.code,
          discount_type: response.data.discount_type,
          discount_value: response.data.discount_value,
          discount_amount: response.data.discount_amount,
          new_total: response.data.new_total
        });
      } else {
        setError(response.data.message);
        onPromoApplied(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la validation du code');
      onPromoApplied(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    setCode('');
    setError('');
    setSuccess('');
    onPromoApplied(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleValidate();
    }
  };

  if (appliedPromo) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="promo-applied">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="text-green-600" size={20} />
            <div>
              <p className="font-medium text-green-800">
                Code <span className="font-mono">{appliedPromo.code}</span> appliqué
              </p>
              <p className="text-sm text-green-600">
                {appliedPromo.discount_type === 'percentage' 
                  ? `-${appliedPromo.discount_value}%` 
                  : `-${appliedPromo.discount_value}€`}
                {' '}→ Réduction de <strong>{appliedPromo.discount_amount.toFixed(2)} €</strong>
              </p>
            </div>
          </div>
          <button
            onClick={handleRemove}
            className="text-gray-500 hover:text-red-500 transition"
            title="Retirer le code promo"
            data-testid="remove-promo-btn"
          >
            <XCircle size={20} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4" data-testid="promo-input-section">
      <div className="flex items-center gap-2 mb-3">
        <Tag className="text-purple-600" size={18} />
        <span className="font-medium text-gray-700">Code promo</span>
      </div>
      
      <div className="flex gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyPress={handleKeyPress}
          placeholder="Entrez votre code"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 font-mono uppercase text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          disabled={loading}
          data-testid="promo-code-input"
        />
        <button
          onClick={handleValidate}
          disabled={loading || !code.trim()}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          data-testid="apply-promo-btn"
        >
          {loading ? (
            <Loader2 className="animate-spin" size={16} />
          ) : (
            'Appliquer'
          )}
        </button>
      </div>

      {error && (
        <div className="mt-2 flex items-center gap-2 text-red-600 text-sm" data-testid="promo-error">
          <XCircle size={16} />
          {error}
        </div>
      )}

      {success && !appliedPromo && (
        <div className="mt-2 flex items-center gap-2 text-green-600 text-sm" data-testid="promo-success">
          <CheckCircle size={16} />
          {success}
        </div>
      )}
    </div>
  );
};

export default PromoCodeInput;
